import * as React from 'react'
import { Route, Switch, Redirect, useParams, useHistory, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Invoices from './Invoices'
import Expenses from './Expenses'
import Transactions from './Transactions'
import Integrations from './Integrations'
import Clients from './Clients'
import Account from './Account'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import ERouteAccountant, { workspacePath } from '../../accountant/ERouteAccountant'
import { AppSidebar } from '../../components/app-sidebar'
import { SiteHeader } from '../../components/site-header'
import { SidebarInset, SidebarProvider } from '../../components/ui/sidebar'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { SessionController, ConnectionsController } from '../../controllers/accountant'
import { AppState } from '../../store'
import { AccountantConnection, AccountantCurrentUser, AccountantWorkspace } from '../../types'

const lastWorkspaceKey = (accountantId: string) => `accountant:${accountantId}:last-workspace`

const readLastWorkspace = (accountantId: string): string | null => {
  try { return window.localStorage.getItem(lastWorkspaceKey(accountantId)) } catch { return null }
}

const writeLastWorkspace = (accountantId: string, workspaceId: string | null) => {
  try {
    if (workspaceId) window.localStorage.setItem(lastWorkspaceKey(accountantId), workspaceId)
    else window.localStorage.removeItem(lastWorkspaceKey(accountantId))
  } catch { /* storage unavailable */ }
}

interface IStateToProps {
  currentAccountant: AccountantCurrentUser
}

interface ShellProps {
  currentAccountant: AccountantCurrentUser
  connections: AccountantConnection[]
  reloadConnections: () => Promise<void>
}

const WorkspaceRoutes = ({ currentAccountant, workspaces, onNotFound }: {
  currentAccountant: AccountantCurrentUser
  workspaces: AccountantWorkspace[]
  onNotFound: () => void
}) => {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const workspace = workspaces.find(w => w.id === workspaceId)

  React.useEffect(() => {
    if (workspace) writeLastWorkspace(currentAccountant.id, workspace.id)
  }, [workspace?.id])

  if (!workspace) return <Redirect to={ERouteAccountant.PATH_CLIENTS} />

  return (
    <Switch>
      <Route path={ERouteAccountant.PATH_INVOICES} render={() => <Invoices workspace={workspace} onNotFound={onNotFound} />} />
      <Route path={ERouteAccountant.PATH_EXPENSES} render={() => <Expenses workspace={workspace} onNotFound={onNotFound} />} />
      <Route path={ERouteAccountant.PATH_TRANSACTIONS} render={() => <Transactions workspace={workspace} onNotFound={onNotFound} />} />
      <Route path={ERouteAccountant.PATH_INTEGRATIONS} render={() => <Integrations workspace={workspace} onNotFound={onNotFound} />} />
      <Redirect to={workspacePath(workspace.id)} />
    </Switch>
  )
}

const Shell = ({ currentAccountant, connections, reloadConnections }: ShellProps) => {
  const history = useHistory()
  const location = useLocation()
  const workspaces = connections.filter(c => c.status === 'accepted').map(c => c.workspace)
  const workspaceMatch = location.pathname.match(/^\/workspaces\/([^/]+)/)
  const currentWorkspace = workspaceMatch ? workspaces.find(w => w.id === workspaceMatch[1]) : undefined

  const onNotFound = async () => {
    writeLastWorkspace(currentAccountant.id, null)
    try {
      await reloadConnections()
    } catch (error) {
      console.error(error)
    }
    history.replace(ERouteAccountant.PATH_CLIENTS)
  }

  const logout = async () => {
    try {
      await SessionController.logout()
    } finally {
      window.location.assign('/sign_in')
    }
  }

  const lastWorkspaceId = readLastWorkspace(currentAccountant.id)
  const defaultPath = workspaces.some(w => w.id === lastWorkspaceId)
    ? workspacePath(lastWorkspaceId)
    : workspaces.length > 0 ? workspacePath(workspaces[0].id) : ERouteAccountant.PATH_CLIENTS

  return (
    <SidebarProvider
      style={{ '--sidebar-width': 'calc(var(--spacing) * 64)', '--header-height': 'calc(var(--spacing) * 12)' } as React.CSSProperties}
    >
      <AppSidebar workspaces={workspaces} currentWorkspace={currentWorkspace} email={currentAccountant.email} onSignOut={logout} />
      <SidebarInset>
        <SiteHeader />
        <div className='flex flex-1 flex-col'>
          <div className='@container/main flex flex-1 flex-col p-4 lg:p-6'>
            <Switch>
              <Route path={ERouteAccountant.PATH_CLIENTS} render={() => <Clients connections={connections} onChange={reloadConnections} />} />
              <Route path={ERouteAccountant.PATH_ACCOUNT} component={Account} />
              <Route path={ERouteAccountant.PATH_WORKSPACE} render={() => <WorkspaceRoutes currentAccountant={currentAccountant} workspaces={workspaces} onNotFound={onNotFound} />} />
              <Redirect to={defaultPath} />
            </Switch>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

const Dashboard = ({ currentAccountant }: IStateToProps) => {
  const { t } = useTranslation()
  const [connections, setConnections] = React.useState<AccountantConnection[] | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)

  const reloadConnections = async () => {
    const response = await ConnectionsController.getConnections()
    setConnections(response)
  }

  const loadConnections = () => {
    setLoadFailed(false)
    reloadConnections().catch(error => {
      console.error(error)
      setLoadFailed(true)
    })
  }

  React.useEffect(() => {
    loadConnections()
  }, [])

  if (connections === null && loadFailed) {
    return (
      <div className='flex flex-col items-start gap-4 p-6'>
        <Alert variant='destructive'>
          <AlertTitle>{t('Accountant::Could not load your clients')}</AlertTitle>
          <AlertDescription>{t('Accountant::Something went wrong, please try again later.')}</AlertDescription>
        </Alert>
        <Button onClick={loadConnections}>{t('Accountant::Try again')}</Button>
      </div>
    )
  }

  if (connections === null) {
    return (
      <div className='flex flex-col gap-2 p-6'>
        {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className='h-12 w-full' />)}
      </div>
    )
  }

  return (
    <>
      <ScrollToTopOnMount />
      <Shell currentAccountant={currentAccountant} connections={connections} reloadConnections={reloadConnections} />
    </>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => ({
  currentAccountant: state.authentication.currentAccountant,
})

export default connect(mapStateToProps)(Dashboard)
